import * as AppStateActions from '../actionTypes/app-state';
import { RESET } from '../actionTypes/common';
import { RESET_CONVERSATION } from '../actionTypes/conversation';
import { WIDGET_STATE } from '../constants/app';
import { CONNECTION_STATE } from '../constants/connection-state';

const INITIAL_STATE = {
    isInitialized: false,
    uploadVisible: false,
    settingsVisible: false,
    visibleChannelCategory: null,
    widgetState: WIDGET_STATE.INIT,
    widgetSize: 'md',
    connectNotificationTimestamp: null,
    errorNotificationMessage: null,
    introHeight: 158,
    showAnimation: false,
    isFetchingMoreMessages: false,
    shouldScrollToBottom: true,
    typingEvents: [],
    userIsTyping: false,
    connectionState: CONNECTION_STATE.CONNECTED,
    activeConversationId: 'default',
};

function isTargetEvent(role, id, event) {
    if (role !== event.role) {
        return false;
    }

    return role === 'appMaker' || event.id === id;
}

function addTypingEvent(currentTypingEvents, role, id, typerInfo) {
    const typerIndex = currentTypingEvents.findIndex((event) => isTargetEvent(role, id, event));

    if (typerIndex === -1) {
        return [{ role, id, ...typerInfo }, ...currentTypingEvents];
    }

    // update avatarUrl and name anyway
    currentTypingEvents[typerIndex] = { ...currentTypingEvents[typerIndex], ...typerInfo };
    return [...currentTypingEvents];
}

function removeTypingEvent(currentTypingEvents, role, id) {
    return currentTypingEvents.filter((event) => !isTargetEvent(role, id, event));
}

export default function AppStateReducer(state = INITIAL_STATE, action) {
    switch (action.type) {
        case AppStateActions.SET_ACTIVE_CONVERSATION_ID:
            return { ...state, activeConversationId: action.id };
        case RESET:
            // keep widget state even on a reset
            return {
                ...INITIAL_STATE,
                widgetSize: state.widgetSize,
                activeConversationId: 'default',
            };
        case RESET_CONVERSATION:
            return {
                ...state,
                connectNotificationTimestamp: null,
                activeConversationId: 'default',
            };

        case AppStateActions.SET_INITIALIZATION_STATE:
            return {
                ...state,
                isInitialized: action.value,
            };
        case AppStateActions.ENABLE_IMAGE_UPLOAD:
            return {
                ...state,
                imageUploadEnabled: true,
            };

        case AppStateActions.DISABLE_IMAGE_UPLOAD:
            return {
                ...state,
                imageUploadEnabled: false,
            };

        case AppStateActions.ENABLE_SOUND_NOTIFICATION:
            return {
                ...state,
                soundNotificationEnabled: true,
            };

        case AppStateActions.DISABLE_SOUND_NOTIFICATION:
            return {
                ...state,
                soundNotificationEnabled: false,
            };

        case AppStateActions.SET_EMAIL_READONLY:
            return {
                ...state,
                readOnlyEmail: true,
            };

        case AppStateActions.UNSET_EMAIL_READONLY:
            return {
                ...state,
                readOnlyEmail: false,
            };

        case AppStateActions.OPEN_WIDGET:
            return {
                ...state,
                widgetState: WIDGET_STATE.OPENED,
                showAnimation: true,
            };

        case AppStateActions.CLOSE_WIDGET:
            return {
                ...state,
                visibleChannelCategory: null,
                widgetState: WIDGET_STATE.CLOSED,
                settingsVisible: false,
                showAnimation: true,
            };

        case AppStateActions.SHOW_UPLOAD:
            return {
                ...state,
                uploadVisible: true,
            };

        case AppStateActions.HIDE_UPLOAD:
            return {
                ...state,
                uploadVisible: false,
            };

        case AppStateActions.SHOW_SETTINGS:
            return {
                ...state,
                settingsVisible: true,
            };

        case AppStateActions.HIDE_SETTINGS:
            return {
                ...state,
                settingsVisible: false,
            };

        case AppStateActions.SHOW_CHANNEL_PAGE:
            return {
                ...state,
                visibleChannelCategory: action.channelType,
            };

        case AppStateActions.HIDE_CHANNEL_PAGE:
            return {
                ...state,
                visibleChannelCategory: undefined,
            };

        case AppStateActions.SHOW_CONNECT_NOTIFICATION:
            return {
                ...state,
                connectNotificationTimestamp: action.timestamp,
            };

        case AppStateActions.HIDE_CONNECT_NOTIFICATION:
            return {
                ...state,
                connectNotificationTimestamp: null,
            };

        case AppStateActions.SHOW_ERROR_NOTIFICATION:
            return {
                ...state,
                errorNotificationMessage: action.message,
            };

        case AppStateActions.HIDE_ERROR_NOTIFICATION:
            return {
                ...state,
                errorNotificationMessage: null,
            };

        case AppStateActions.SET_EMBEDDED:
            return {
                ...state,
                widgetState: action.value ? WIDGET_STATE.EMBEDDED : WIDGET_STATE.INIT,
                widgetSize: action.value ? 'embedded' : state.widgetSize,
            };

        case AppStateActions.SET_INTRO_HEIGHT:
            return {
                ...state,
                introHeight: action.value,
            };

        case AppStateActions.DISABLE_ANIMATION:
            return {
                ...state,
                showAnimation: false,
            };

        case AppStateActions.SET_FETCHING_MORE_MESSAGES:
            return {
                ...state,
                isFetchingMoreMessages: action.value,
            };
        case AppStateActions.SET_SHOULD_SCROLL_TO_BOTTOM:
            return {
                ...state,
                shouldScrollToBottom: action.value,
            };

        case AppStateActions.ADD_TYPING_EVENT: {
            return {
                ...state,
                typingEvents: addTypingEvent(
                    state.typingEvents,
                    action.typingEvent.role,
                    action.typingEvent.id,
                    action.typingEvent.typerInfo,
                ),
            };
        }
        case AppStateActions.REMOVE_TYPING_EVENT: {
            const typingEvents = removeTypingEvent(state.typingEvents, action.typingEvent.role, action.typingEvent.id);
            return {
                ...state,
                typingEvents,
            };
        }
        case AppStateActions.CLEAN_TYPING_EVENTS:
            return {
                ...state,
                typingEvents: [],
            };
        case AppStateActions.UPDATE_WIDGET_SIZE:
            return {
                ...state,
                widgetSize: action.size,
            };
        case AppStateActions.SET_USER_IS_TYPING:
            return {
                ...state,
                userIsTyping: action.isTyping,
            };
        case AppStateActions.SET_CONNECTION_STATE:
            return {
                ...state,
                connectionState: action.connectionState,
            };
        case AppStateActions.SET_BLOCK_CHAT_INPUT:
            return {
                ...state,
                blockChatInput: action.isBlocked,
            };
        default:
            return state;
    }
}
